import { FC } from "react";
import { css, styled } from "styled-components";
import { button, colors, font } from "../lib/sharedStyles";
import { PointerDownLight } from "@fenderdigital/ui/Icons";

interface StyledLinkProps {
  $brand: string;
  $displayType?: string;
  $hasImage: boolean;
  $inNavRow: boolean;
  $inSupportNavDropdown: boolean;
  $level: number;
}

const StyledLink = styled.a<StyledLinkProps>`
  display: flex;
  text-decoration: none;
  transition: all 200ms ease;
  ${font('Instrument Sans', 600, '12px', '20px', '0.12px')};

  &:not([href]) {
    cursor: initial;
    pointer-events: none;
  }

  ${({ $brand }) => $brand == 'jackson' && (
    font('Palanquin Dark', 600, '12px', 'normal')
  )}

  ${({ $brand, $level }) => $level == 1 && $brand == 'fender' && `
    height: 50px;
    align-items: center;
    color: ${colors.fender.primaryDark};
  `}

  ${({ $brand, $level }) => $level == 3 && $brand == 'fender' && `
    font-size: 14px;
    font-weight: 400;
    margin-bottom: 10px;

    &:hover {
      span {
        text-decoration: underline;
      }
    }
  `}

  ${({ $brand, $level }) => $level == 1 && $brand == 'jackson' && `
    height: 64px;
    align-items: center;
    color: #aaa;
    letter-spacing: .07812rem;
    text-transform: uppercase;
    border-bottom: 4px solid transparent;
  `}

  ${({ $brand, $level }) => $level == 2 && $brand == 'jackson' && `
    color: #fff;
    font-size: 18px;
    font-weight: 900;
    line-height: 22px;
    padding-bottom: 24px;
    letter-spacing: .07812rem;
    text-transform: uppercase;
    border-bottom: 1px solid #353535;
  `}

  ${({ $brand, $level }) => $level == 3 && $brand == 'jackson' && `
    flex-direction: column;
    color: #939393;
    font-size: 16px;
    font-weight: 500;

    &:hover {
      color: #fff;
    }
  `}

  // Position title below image in nav rows
  ${({ $inNavRow }) => $inNavRow && `
    flex-direction: column;
    max-width: 168px;

    > span {
     order: 1;
    }
  `}

  ${({ $inNavRow, $level }) => $inNavRow && $level == 3 && `
    > span {
      font-size: 12px;
      line-height: 18px;
    }

    > span:first-of-type {
      font-weight: 600;
      margin-bottom: 4px;
    }
  `}

  svg { // <PointerDownLight />
    margin-left: 4px;
  }

  ${({ $inSupportNavDropdown }) => $inSupportNavDropdown && SupportNavStyles}
`;

const SupportNavStyles = css<StyledLinkProps>`
  flex-direction: column;

  img {
    order: -1;
  }

  ${({ $hasImage }) => $hasImage && `
    > span:first-of-type {
      font-weight: 600;
      margin-top: 10px;
    }

    > span:last-of-type {
      font-weight: 400;
      font-size: 12px;
      line-height: 18px;
      letter-spacing: 0.12px;
    }

    &:hover {
      span {
        text-decoration: none;
      }
    }
  `}

  ${({ $hasImage, $displayType }) => $hasImage && $displayType == '' && `
    > span:last-of-type {
      margin-bottom: 6px;
    }
  `}

  // Normal links
  ${({ $hasImage, $displayType }) => !$hasImage && $displayType == '' && `
    > span {
      font-size: 12px;
      line-height: 18px;
      letter-spacing: 0.12px;
    }
  `}

  ${({ $displayType }) => $displayType == 'App Link' && `
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: repeat(2, 1fr);
    align-items: flex-end;
    border-top: 1px solid #EFF0F0;
    margin-bottom: 18px;

    > img {
      grid-area: 1 / 1 / 3 / 2;
    }

    > span:first-of-type {
      grid-area: 1 / 2 / 2 / 5;
    }

    > span:last-of-type {
      grid-area: 2 / 2 / 3 / 5;
      margin-bottom: 2px;
    }

    &:last-of-type {
      margin-bottom: 40px;
    }
  `}

  ${({ $displayType }) => $displayType == 'Article' && `
    flex-direction: row;
    align-items: center;
    margin: 0;
    padding: 16px 0;
    border-bottom: 1px solid #EFF0F0;

    &:first-of-type {
      padding-top: 8px;
    }

    &:nth-of-type(3) {
      border-bottom: none;
    }

    img {
      width: 58px;
      margin-right: 10px;
    }
      
    > span:first-of-type {
      font-weight: 600;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 0.14px;
    }
  `}

  ${({ $displayType }) => $displayType == 'Button' && (
    button('Black Pill', 'fender', true)
  )}

  ${({ $displayType }) => $displayType == 'Button' && `
    margin: 8px 0 18px;
    width: 100%;

    &:hover {
      span {
        text-decoration: none;
      }
    }
  `}
`;

const NavRowImageWrapper = styled.div<{ $isProduct: boolean; $noRotate?: boolean; }>`
  width: 168px;
  height: 168px;
  overflow: hidden;
  margin-bottom: 16px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }

  ${({ $isProduct }) => $isProduct && `
    background: linear-gradient(180deg, #E5E5E5 0%, #E5E5E5 13.7%, #EFF0F0 59.6%, #F7F7F7 100%);

    img {
      object-fit: contain;
      transform: rotate(-90deg);
      padding: 16px;
      width: calc(100% - 32px);
      height: calc(100% - 32px);
      filter: drop-shadow(12px 12px 10px #ccc);
    }
  `}

  ${({ $isProduct, $noRotate }) => $isProduct && $noRotate && `
    img {
      transform: rotate(0deg);
    }
  `}
`;

interface Props {
  brand: string;
  item: any;
  level: number;
  inNavRow?: boolean;
  inSupportNavDropdown?: boolean;
}

const DesktopMenuLink: FC<Props> = ({ brand, item, level, inNavRow = false, inSupportNavDropdown = false }) => {
  const isFmicAssetsImage = (url: string): boolean => {
    return url.includes('www.fmicassets.com');
  };

  const getResizedImageUrl = (url: string): string => {
    return isFmicAssetsImage(url) ? `https://www.fender.com/cdn-cgi/image/format=auto,resize=height=auto,width=500/${url}` : `${url}`;
  };

  let titleHtml = item.fields.title;
  titleHtml = titleHtml.replaceAll(/(<b>|<\/b>)/g, '');

  return (
    <StyledLink 
      $level={level}
      $brand={brand}
      $inNavRow={inNavRow}
      href={item.fields.url} 
      $hasImage={(item.fields.asset)}
      $displayType={item.fields?.displayType || ''}
      $inSupportNavDropdown={inSupportNavDropdown}
      target={item.fields.openInNewWindow ? "_blank" : "_self"}
      data-menu-link-display-type={item.fields?.displayType || ''}
    >
      {level == 1 && (
        <span className="level-1-target" data-navigation-flyout={titleHtml} dangerouslySetInnerHTML={{__html: titleHtml}} />
      )}
      {level == 2 && (
        <span data-navigation-group={titleHtml} dangerouslySetInnerHTML={{__html: titleHtml}} />
      )}
      {level == 3 && (
        <span className="level-3-target" data-navigation-label={titleHtml} dangerouslySetInnerHTML={{__html: titleHtml}} />
      )}

      {(brand == 'jackson' && level == 1 && item.fields.subItems) && (
        <PointerDownLight fill="#fff" fontSize="8px" />
      )}
      {(level == 3 && item.fields.asset) && (
        <>
          {(inNavRow) ? (
            // @ts-ignore
            <NavRowImageWrapper>
              <img src={getResizedImageUrl(`https:${item.fields.asset.fields.file?.url}`)} />
            </NavRowImageWrapper>
          ) : (
            <img src={getResizedImageUrl(`https:${item.fields.asset.fields.file?.url}`)} />
          )}
        </>
      )}
      {(level == 3 && item.fields.productImageUrl) && (
        <NavRowImageWrapper $isProduct $noRotate={(item.fields.productImageUrl.includes('nr.png'))}>
          <img src={getResizedImageUrl(item.fields.productImageUrl)} />
        </NavRowImageWrapper>
      )}

      {(item.fields.description && (inNavRow || inSupportNavDropdown)) && (
        <span dangerouslySetInnerHTML={{__html: item.fields.description}} />
      )}
    </StyledLink>
  );
}

export default DesktopMenuLink;